import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline, TextLabel2 } from '@cbhq/cds-web/typography';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { memo, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { AssetConfig } from '@/onchain/assets';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import { DisplayType, format } from '@/utils/numberFormatter';
import { getOrderedCurrenciesForPool } from '@/utils/tokenPreference';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/utils/featureFlags';

type Props = {
  pool: ReturnType<typeof usePoolsInfo>['data']['poolsInfo'][number];
};

/**
 * PoolsListMobileRow - Mobile view for a single pool in the pools list
 *
 * Displays pool information in a compact ListCell format optimized for mobile screens.
 * Shows token pair, fee tier, USDC rewards badge (if applicable), TVL, and APR.
 */
export const PoolsListMobileRow = memo(function PoolsListMobileRow({ pool }: Props) {
  const isFeatureFlagEnabled = useFeatureFlag(
    FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE,
  );
  const router = useRouter();
  const { poolId, currencies, name, fee, tvlNotional, apr7dPercentage } = pool;

  const feePercent = useMemo(() => {
    // fee / fee basis points / percent representation
    return fee / 10_000 / 100;
  }, [fee]);

  // Use shared utility to order currencies consistently with pool name
  const orderedCurrencies = useMemo(() => {
    return getOrderedCurrenciesForPool(currencies);
  }, [currencies]);

  const isUSDCTokenPair = currencies.some((currency) => currency.displaySymbol === 'USDC');

  const handlePress = useCallback(() => {
    if (!isFeatureFlagEnabled) return;
    void router.push(`/pools/${poolId}`);
  }, [router, poolId, isFeatureFlagEnabled]);

  return (
    <ListCell
      testID={`pool-row-${poolId}`}
      justifyContent="space-between"
      onClick={handlePress}
      media={<PoolsListTokensMedia size="xl" assets={orderedCurrencies as AssetConfig[]} />}
      title={<TextHeadline as="span">{name}</TextHeadline>}
      description={
        <HStack gap={0.5}>
          <Tag intent="informational" colorScheme="gray">
            {format({ value: feePercent, type: DisplayType.Percentage })}
          </Tag>
          {isUSDCTokenPair && <USDCRewardsChip showTooltip={false} />}
        </HStack>
      }
      detail={
        <VStack alignItems="flex-end" gap={0.5}>
          <TextBody as="span">
            {format({ value: tvlNotional, type: DisplayType.FiatShort })}
          </TextBody>
          <TextLabel2 as="span" color="fgMuted">
            {format({ value: apr7dPercentage, type: DisplayType.Percentage })} APR
          </TextLabel2>
        </VStack>
      }
    />
  );
});
